// i18n area 'credentials' (M2g, 2026-08-03 — the typed keystore): the
// Credentials page, its per-kind create/edit/rotate dialogs and the usedBy
// census vocabulary. The write-only discipline shows up in the COPY too: the
// UI states that secrets are never rendered back, because the render truly has
// no secret to show.

export const en = {
  credentials: {
    title: 'Credentials',
    createTitle: 'Create credential',
    name: 'Name',
    nameHint: 'Lowercase letters, digits and dashes',
    kind: 'Kind',
    kindImmutableHint: 'The kind is fixed at creation — retyping a live secret is a NEW credential, never an edit',
    kinds: {
      iscsiChap: 'iSCSI CHAP',
      bmcAccount: 'BMC / device account',
      cephx: 'Ceph (cephx)',
      luksKey: 'LUKS key',
      s3Account: 'S3 account',
      replicationToken: 'Replication token',
      cephfsMirrorBootstrap: 'CephFS mirror bootstrap (minted by the peering, consumed on collect)',
      fileUser: 'File user (NAS)',
      tlsCertificate: 'TLS certificate',

      // #181 T5: the contract has 13 kinds — a label missing here rendered
      // the raw i18n key in the kind chip.
      smtpAccount: 'SMTP account',
      webhookSecret: 'Webhook secret',
      wireguardKey: 'WireGuard key',
      machineAccount: 'Machine account (domain)',
      directoryAccount: 'Directory service account'
    },
    kindHints: {
      iscsiChap: "CHAP identity for iSCSI targets — referenced by a target's CHAP setting",
      bmcAccount:
        'Management-device account (BMC, switch, PDU) — referenced by the fencing cascade; its username is also the account the self-disarm degrades',
      cephx: 'Ceph client identity — declared for the Ceph engine round (M3), no consumer admits it yet',
      luksKey:
        'At-rest LUKS2 passphrase — minted by the platform for encrypted vdisks and dm-crypt OSDs; the recovery export below is the one way it ever leaves the keystore',
      s3Account:
        'S3 access-key pair — referenced by S3 backup targets; endpoint and bucket live on the target, the keystore holds only the identity',
      replicationToken:
        "DR link secret (tvsl_…) minted by the destination cluster's operator — presented as the bearer on its internal receive plane, never a user session",
      cephfsMirrorBootstrap:
        "cephfs-mirror bootstrap token (a cephx key inside) minted by the peering of a file replication link from another cluster — platform-only, encrypted, consumed by the source's one collect (ENEA-A-R9)",
      fileUser:
        'Local data-plane user for SMB/SFTP/FTP file shares — password and/or SSH public keys. Domain identities exist too (NAS-2): a share can name directory principals instead — the local file user remains the domain-less path',
      tlsCertificate:
        'TLS server pair for data endpoints (OBJ-1) — public PEM chain plus write-only private key; an object store without one serves with the cluster certificate',
      directoryAccount:
        'The account the appliance authenticates AS against a directory (NAS-2): the JOIN account on Active Directory, the BIND account on LDAP — a DirectoryRealm references it by id'
    },
    identity: 'Account / identity',
    writeOnlyNotice:
      'Secrets are WRITE-ONLY: stored envelope-encrypted, they can be rotated but never displayed again — by anyone.',
    secret: 'Secret',
    secretNever: 'Write-only — stored envelope-encrypted; Rotate replaces it, nothing ever displays it.',
    username: 'Username',
    password: 'Password',
    chapPasswordHint: '12–255 characters — 12–16 is the interoperable sweet spot (some initiators cap at 16)',
    mutual: 'Mutual pair (bidirectional CHAP)',
    mutualHint: 'The target-side identity for mutual CHAP — the pair travels together',
    mutualUsername: 'Mutual username',
    mutualPassword: 'Mutual password',
    mutualPasswordHint: 'Must differ from the CHAP password (one secret per direction)',
    bmcUsernameHint:
      "The account on the device — also the account whose role the self-disarm degrades on the peer's device",
    certificateFingerprint: 'Device certificate fingerprint',
    certificateFingerprintHint:
      'sha-256 of the device TLS certificate, as `openssl` prints it (colons optional). Redfish fencing REFUSES to run without it: an unverified call hands the account that powers nodes off to whoever is listening. Leave it empty only for an account that speaks no TLS — a switch, a PDU, an IPMI-only BMC.',
    entity: 'Entity',
    entityHint: 'The cephx entity name — a client. prefix is tolerated',
    key: 'Key',
    keyHint: 'The base64 cephx key',
    passphrase: 'Passphrase',
    passphraseHint: 'Optional — leave it empty and the platform mints one no operator ever sees',
    accessKeyId: 'Access key ID',
    accessKeyIdHint: 'The public half of the S3 key pair',
    secretAccessKey: 'Secret access key',
    secretAccessKeyHint: 'Write-only — backup tasks resolve it at use time; rotation takes effect on the next run',
    clusterName: 'Peer cluster name',
    clusterNameHint:
      "The peer cluster this token speaks for — cross-checked against the replication link's expected name",
    fileUsernameHint: 'The login name SMB/SFTP clients present — lowercase, unique across file users cluster-wide',
    fileUserPasswordHint:
      'At least 8 characters — optional when SSH keys are present (a key-only SFTP user); SMB references require it',
    sshPublicKeys: 'SSH public keys',
    sshPublicKeysHint:
      'OpenSSH authorized_keys lines, one per line — public halves only, used by SFTP; SMB and NFS ignore them',
    fileUserNeedsSecret:
      'A password or at least one SSH public key is required — a user nobody can authenticate as is refused',
    directoryUsername: 'Directory account name',
    directoryUsernameHint:
      "In whichever spelling the directory wants: administrator@CORP.TEST or CORP\\administrator for an AD join, a full bind DN (cn=tvstor,ou=services,dc=example,dc=com) for an LDAP bind — public, so you can recognise the appliance in the directory's own audit log",
    directoryPasswordHint:
      "The account's password in the CUSTOMER'S directory — its policy governs, so no shape is imposed here; replaceable only through Rotate",
    token: 'Token',
    tokenHint:
      'The tvsl_ bearer secret, at least 24 characters — minted by the destination operator, exchanged out of band',
    rotateTokenHint:
      'Rotate on BOTH clusters, destination first — the receive endpoint verifies against its current entry only.',
    mintedChip: 'Platform-minted',
    suppliedChip: 'Operator-supplied',
    usedBy: 'Used by',
    unused: 'Not referenced',
    referrers: {
      target: 'Target',
      clusterNode: 'Cluster node',
      backupTarget: 'Backup target',
      replicationLink: 'Replication link',
      vdisk: 'Vdisk (LUKS key)',
      cephcluster: 'Ceph cluster (OSD key)',
      fileShare: 'File share',
      objectStore: 'Object store',
      s3Account: 'S3 account',
      alertSink: 'Alert sink',
      directoryRealm: 'Directory realm'
    },
    tabOverview: 'Overview',
    tabUsedBy: 'Used by',
    usedByEmpty: 'No referrer — this credential can be deleted freely.',
    editTitle: 'Edit credential {name}',
    editHint: 'Public fields only — secret material moves exclusively through Rotate (sudo).',

    // #181 ondata 1c (SEC-REVOKE #79 «revoca HARD»): throw a fileUser out of
    // the web explorer — the lever that closes the T8 dead end (deleting the
    // credential is refused with 409 in_use while shares reference it).
    revokeSessionsAction: 'Revoke web sessions',
    revokeSessionsTitle: 'Throw {name} out of the web explorer?',
    revokeSessionsBody:
      'Every live web-explorer session of this file-services account is revoked, on every node — and the document-editor tokens those sessions minted are destroyed: an open editor stops saving within seconds, unsaved work is LOST.',
    revokeSessionsBoundary:
      "Kicking out is not disabling: the account keeps existing and can log in again. Remove it from the shares' user lists first when the intent is that it stays out. SMB/SFTP/FTP protocol sessions are not touched — this lever is the web plane's.",
    revokeSessionsConfirm: 'Revoke now',
    revokeSessionsDone: '{sessions} session(s) revoked, {tokens} editor token(s) destroyed.',
    rotate: 'Rotate secret',
    rotateTitle: 'Rotate the secret of {name}',
    rotateHint:
      'The new secret is encrypted and replicated; consumers converge — referring targets are re-applied on their ACLs, fencing reads the new secret at next use.',
    rotateChapWarning:
      'Rotating CHAP logs out initiators that do not present the new secret — coordinate with the consumer side.',
    rotateBmcHint:
      'Rotate records what the platform must present — changing the password ON the device is your act: do both.',

    // A7 (#84, ratifica Luca 2026-08-12): a luksKey in use cannot be rotated —
    // the volumes encrypted with it would stop opening. The API already refuses
    // with a talking 409; offering the action anyway made the UI a dead end.
    rotateBlockedLuksInUse:
      'A LUKS key in use cannot be rotated: the volumes encrypted with it would no longer open. Detach the consumers listed under Used by first.',

    // #181 T5: the kinds whose rotate the console does not carry yet used to
    // open a field-less dialog that submitted ANOTHER kind's block (a certain
    // 422) — now the button says the truth before the click.
    rotateUnavailableKind:
      'Rotating this kind of credential is not available from the console yet — use the CLI (tvstor credential rotate).',
    rotateBlockedMachineAccount:
      'The machine account secret is negotiated with the directory — the ordinary rotate is not an operator gesture. Use the dedicated rotation (CLI: rotateMachineAccount).',
    deleteTitle: 'Delete credential {name}?',
    deleteWarning:
      'The encrypted secret goes with the row. A referenced credential is refused (409) naming its referrers.',
    deleteInUse: 'This credential is referenced — detach the consumers first:',
    exportRecovery: 'Export recovery keys',
    exportRecoveryTitle: 'Export the at-rest recovery keys',
    exportRecoveryWarning:
      'The LUKS recovery keys are shown ONCE, in clear, on this screen — the platform keeps only ciphertext and no copy of this export. Print them, put them in the physical safe beside the break-glass credentials, then close this window. Without this export (or the keystore master key), an extracted or decommissioned disk tells nothing — that is the point.',
    exportRecoveryConfirmLabel: 'Cluster name',
    exportRecoveryConfirmHint:
      'Type the cluster name ({name}) to confirm — this puts every at-rest secret on one screen',
    exportRecoveryRun: 'Export now',
    exportRecoveryShownOnce: 'Shown once — nothing on the platform will ever display these again.',
    exportRecoveryEmpty: 'No LUKS keys in the keystore yet — nothing to put in the safe.',
    exportRecoveryKeyCount: '{count} key(s) exported at {at}',
    exportRecoveryPassphrase: 'Passphrase'
  }
}

export const it: typeof en = {
  credentials: {
    title: 'Credenziali',
    createTitle: 'Crea credenziale',
    name: 'Nome',
    nameHint: 'Minuscole, cifre e trattini',
    kind: 'Tipo',
    kindImmutableHint:
      'Il tipo è fissato alla creazione — ritipizzare un segreto vivo è una NUOVA credenziale, mai una modifica',
    kinds: {
      iscsiChap: 'CHAP iSCSI',
      bmcAccount: 'Account BMC / device',
      cephx: 'Ceph (cephx)',
      luksKey: 'Chiave LUKS',
      s3Account: 'Account S3',
      replicationToken: 'Token di replica',
      cephfsMirrorBootstrap: 'Bootstrap del mirror CephFS (coniato dal peering, consumato alla consegna)',
      fileUser: 'Utente file (NAS)',
      tlsCertificate: 'Certificato TLS',
      smtpAccount: 'Account SMTP',
      webhookSecret: 'Segreto webhook',
      wireguardKey: 'Chiave WireGuard',
      machineAccount: 'Machine account (dominio)',
      directoryAccount: 'Account di servizio directory'
    },
    kindHints: {
      iscsiChap: "Identità CHAP per i target iSCSI — referenziata dall'impostazione CHAP di un target",
      bmcAccount:
        "Account di un device di management (BMC, switch, PDU) — referenziato dalla cascata di fencing; il suo username è anche l'account che il self-disarm degrada",
      cephx:
        'Identità client Ceph — dichiarata per il round del motore Ceph (M3), nessun consumatore la ammette ancora',
      luksKey:
        "Passphrase LUKS2 at-rest — coniata dalla piattaforma per i vdisk cifrati e gli OSD dm-crypt; l'export di ripristino qui sotto è l'unica via con cui lascia il keystore",
      s3Account:
        "Coppia di chiavi S3 — referenziata dalle destinazioni backup S3; endpoint e bucket vivono sulla destinazione, il keystore tiene solo l'identità",
      replicationToken:
        "Segreto del link DR (tvsl_…) coniato dall'operatore del cluster di destinazione — presentato come bearer al suo piano interno di ricezione, mai una sessione utente",
      cephfsMirrorBootstrap:
        "Token di bootstrap di cephfs-mirror (dentro una chiave cephx) coniato dal peering di un link di replica file di un altro cluster — solo di piattaforma, cifrato, consumato dall'unico ritiro della sorgente (ENEA-A-R9)",
      fileUser:
        "Utente locale del piano dati per le condivisioni file SMB/SFTP/FTP — password e/o chiavi pubbliche SSH. Esistono anche le identità di dominio (NAS-2): una condivisione può nominare principal di directory — l'utente file locale resta la via senza dominio",
      tlsCertificate:
        'Coppia server TLS per gli endpoint dati (OBJ-1) — catena PEM pubblica più chiave privata solo-scrittura; un object store senza serve col certificato di cluster',
      directoryAccount:
        "L'account con cui l'appliance si autentica verso una directory (NAS-2): l'account di JOIN su Active Directory, l'account di BIND su LDAP — un DirectoryRealm lo referenzia per id"
    },
    identity: 'Account / identità',
    writeOnlyNotice:
      'I segreti sono SOLO-SCRITTURA: conservati cifrati a busta, si possono ruotare ma mai più visualizzare — da nessuno.',
    secret: 'Segreto',
    secretNever: 'Solo-scrittura — conservato cifrato a busta; la Rotazione lo sostituisce, niente lo mostra mai.',
    username: 'Nome utente',
    password: 'Password',
    chapPasswordHint: '12–255 caratteri — 12–16 è la zona interoperabile (alcuni initiator si fermano a 16)',
    mutual: 'Coppia mutual (CHAP bidirezionale)',
    mutualHint: "L'identità lato target per il CHAP mutual — la coppia viaggia insieme",
    mutualUsername: 'Nome utente mutual',
    mutualPassword: 'Password mutual',
    mutualPasswordHint: 'Deve differire dalla password CHAP (un segreto per direzione)',
    bmcUsernameHint:
      "L'account sul device — anche l'account il cui ruolo viene degradato dal self-disarm sul device del peer",
    certificateFingerprint: 'Impronta del certificato del device',
    certificateFingerprintHint:
      "sha-256 del certificato TLS del device, come lo stampa `openssl` (i due punti sono facoltativi). Senza, il fencing Redfish RIFIUTA di partire: una chiamata non verificata regala a chi ascolta l'account che spegne i nodi. Lasciala vuota solo per un account che non parla TLS — uno switch, una PDU, un BMC solo-IPMI.",
    entity: 'Entity',
    entityHint: 'Il nome entity cephx — il prefisso client. è tollerato',
    key: 'Chiave',
    keyHint: 'La chiave cephx in base64',
    passphrase: 'Passphrase',
    passphraseHint: 'Opzionale — lasciala vuota e la piattaforma ne conia una che nessun operatore vede',
    accessKeyId: 'Access key ID',
    accessKeyIdHint: 'La metà pubblica della coppia di chiavi S3',
    secretAccessKey: 'Secret access key',
    secretAccessKeyHint:
      "Solo-scrittura — i task di backup la risolvono al momento dell'uso; la rotazione vale dal prossimo run",
    clusterName: 'Nome del cluster peer',
    clusterNameHint:
      'Il cluster peer per cui parla questo token — verificato contro il nome atteso del link di replica',
    fileUsernameHint:
      'Il nome di login che i client SMB/SFTP presentano — minuscolo, unico fra gli utenti file di tutto il cluster',
    fileUserPasswordHint:
      'Almeno 8 caratteri — opzionale quando ci sono chiavi SSH (un utente SFTP solo-chiave); i riferimenti SMB la richiedono',
    sshPublicKeys: 'Chiavi pubbliche SSH',
    sshPublicKeysHint:
      'Righe authorized_keys OpenSSH, una per riga — solo metà pubbliche, usate da SFTP; SMB e NFS le ignorano',
    fileUserNeedsSecret:
      'Serve una password o almeno una chiave pubblica SSH — un utente con cui nessuno può autenticarsi viene rifiutato',
    directoryUsername: "Nome dell'account di directory",
    directoryUsernameHint:
      "Nella grafia che la directory vuole: administrator@CORP.TEST o CORP\\administrator per un join AD, un bind DN completo (cn=tvstor,ou=services,dc=example,dc=com) per un bind LDAP — pubblico, così riconosci l'appliance nell'audit log della directory stessa",
    directoryPasswordHint:
      "La password dell'account nella directory DEL CLIENTE — governa la sua policy, quindi qui non si impone alcuna forma; sostituibile solo con la Rotazione",
    token: 'Token',
    tokenHint:
      "Il segreto bearer tvsl_, almeno 24 caratteri — coniato dall'operatore della destinazione, scambiato fuori banda",
    rotateTokenHint:
      "Ruota su ENTRAMBI i cluster, prima la destinazione — l'endpoint di ricezione verifica solo contro la propria voce corrente.",
    mintedChip: 'Coniata dalla piattaforma',
    suppliedChip: "Fornita dall'operatore",
    usedBy: 'Usata da',
    unused: 'Non referenziata',
    referrers: {
      target: 'Target',
      clusterNode: 'Nodo del cluster',
      backupTarget: 'Destinazione backup',
      replicationLink: 'Link di replica',
      vdisk: 'Vdisk (chiave LUKS)',
      cephcluster: 'Cluster Ceph (chiave OSD)',
      fileShare: 'Condivisione file',
      objectStore: 'Object store',
      s3Account: 'Account S3',
      alertSink: 'Sink di allarme',
      directoryRealm: 'Realm di directory'
    },
    tabOverview: 'Panoramica',
    tabUsedBy: 'Usata da',
    usedByEmpty: 'Nessun riferimento — questa credenziale si può eliminare liberamente.',
    editTitle: 'Modifica la credenziale {name}',
    editHint: 'Solo i campi pubblici — il materiale segreto passa esclusivamente dalla Rotazione (sudo).',
    revokeSessionsAction: 'Revoca le sessioni web',
    revokeSessionsTitle: "Cacciare {name} dall'esploratore web?",
    revokeSessionsBody:
      "Ogni sessione web viva di questo account di servizi file viene revocata, su ogni nodo — e i token dell'editor documenti coniati da quelle sessioni vengono distrutti: un editor aperto smette di salvare entro pochi secondi, il lavoro non salvato è PERSO.",
    revokeSessionsBoundary:
      "Cacciare non è disabilitare: l'account continua a esistere e può rifare il login. Toglilo prima dalle liste utenti delle condivisioni se l'intento è che resti fuori. Le sessioni di protocollo SMB/SFTP/FTP non vengono toccate — questa leva è del piano web.",
    revokeSessionsConfirm: 'Revoca ora',
    revokeSessionsDone: '{sessions} sessioni revocate, {tokens} token editor distrutti.',
    rotate: 'Ruota il segreto',
    rotateTitle: 'Ruota il segreto di {name}',
    rotateHint:
      'Il nuovo segreto viene cifrato e replicato; i consumatori convergono — i target che la referenziano vengono ri-applicati sulle ACL, il fencing legge il nuovo segreto al prossimo uso.',
    rotateChapWarning:
      'Ruotare il CHAP disconnette gli initiator che non presentano il nuovo segreto — coordina il lato consumer.',
    rotateBmcHint:
      'La rotazione registra ciò che la piattaforma deve presentare — cambiare la password SUL device è un tuo atto: falli entrambi.',
    rotateBlockedLuksInUse:
      'Una chiave LUKS in uso non si può ruotare: i volumi cifrati con essa non si aprirebbero più. Sgancia prima i consumatori elencati in «Usata da».',
    rotateUnavailableKind:
      'La rotazione di questo tipo di credenziale non è ancora disponibile dalla console — usa la CLI (tvstor credential rotate).',
    rotateBlockedMachineAccount:
      'Il segreto del machine account è negoziato con la directory — la rotazione ordinaria non è un gesto da operatore. Usa la rotazione dedicata (CLI: rotateMachineAccount).',
    deleteTitle: 'Eliminare la credenziale {name}?',
    deleteWarning:
      'Il segreto cifrato se ne va con la riga. Una credenziale referenziata viene rifiutata (409) nominando chi la usa.',
    deleteInUse: 'Questa credenziale è referenziata — sgancia prima i consumatori:',
    exportRecovery: 'Esporta le chiavi di ripristino',
    exportRecoveryTitle: 'Esporta le chiavi di ripristino at-rest',
    exportRecoveryWarning:
      "Le chiavi di ripristino LUKS vengono mostrate UNA VOLTA, in chiaro, su questo schermo — la piattaforma conserva solo testo cifrato e nessuna copia di questo export. Stampale, mettile nella cassaforte fisica accanto alle credenziali d'emergenza, poi chiudi questa finestra. Senza questo export (o la master key del keystore), un disco estratto o dismesso non racconta nulla — ed è esattamente lo scopo.",
    exportRecoveryConfirmLabel: 'Nome del cluster',
    exportRecoveryConfirmHint:
      'Digita il nome del cluster ({name}) per confermare — questo mette ogni segreto at-rest su uno schermo',
    exportRecoveryRun: 'Esporta ora',
    exportRecoveryShownOnce: 'Mostrate una volta sola — niente sulla piattaforma le visualizzerà mai più.',
    exportRecoveryEmpty: 'Nessuna chiave LUKS nel keystore — niente da mettere in cassaforte, per ora.',
    exportRecoveryKeyCount: '{count} chiave/i esportate alle {at}',
    exportRecoveryPassphrase: 'Passphrase'
  }
}
